import React from 'react';
import {
    TextField,
    DatePicker,
    Picker,
    Item,
    Flex
} from '@adobe/react-spectrum';
import { parseDate, CalendarDate } from '@internationalized/date';

type FieldType = '1' | '2' | '3' | '4' | '5';

interface FieldConfig {
    [key: string]: string;
}

interface ValuesData {
    [key: string]: any;
}

interface DynamicProductTypeFieldsProps {
    config?: any | null;
    data?: any | null;
    onChange: (newData: any) => void;
}

type FieldValue = string | CalendarDate | null;

const fieldTypeComponents: {
    [key in FieldType]: (
        key: string,
        label: string,
        value: FieldValue,
        onChange: (fieldKey: string, value: FieldValue) => void,
        options?: string[]
    ) => JSX.Element;
} = {
    '1': (key, label, value, onChange) => (
        <TextField
            key={key}
            label={label}
            value={(value as string) || ''}
            onChange={(val) => onChange(key, val)}
        />
    ),
    '2': (key, label, value, onChange) => (
        <TextField
            key={key}
            label={label}
            type="number"
            value={(value as string) || ''}
            onChange={(val) => onChange(key, val)}
        />
    ),
    '3': (key, label, value, onChange) => (
        <TextField
            key={key}
            label={label}
            type="number"
            value={(value as string) || ''}
            onChange={(val) => onChange(key, val)}
        />
    ),
    '4': (key, label, value, onChange) => (
        <DatePicker
            key={key}
            label={label}
            value={value as CalendarDate | null}
            onChange={(val) => onChange(key, val)}
        />
    ),
    '5': (key, label, value, onChange, options = []) => (
        <Picker
            key={key}
            label={label}
            selectedKey={(value as string) || undefined}
            onSelectionChange={(val) => onChange(key, val as string)}
        >
            {options.map((opt) => (
                <Item key={opt}>{opt}</Item>
            ))}
        </Picker>
    )
};

const DynamicProductTypeFields: React.FC<DynamicProductTypeFieldsProps> = ({
    config = {},
    data = {},
    onChange
}) => {

    function normalizeDateString(value: string): string | null {
        const parts = value.substring(0, 10).split('-');

        if (parts.length !== 3) return null;

        const [a, b, c] = parts;

        // Case 1: YYYY-MM-DD
        if (a.length === 4) {
            return `${a}-${b}-${c}`;
        }

        // Case 2: MM-DD-YYYY
        if (c.length === 4 && parseInt(a) <= 12) {
            return `${c}-${a}-${b}`;
        }

        // Case 3: DD-MM-YYYY
        if (c.length === 4 && parseInt(b) <= 12) {
            return `${c}-${b}-${a}`;
        }

        return null;
    }

    const renderFields = () => {
        const fields: JSX.Element[] = [];

        for (let i = 0; i < 10; i++) {
            const key = `field${i}`;
            const type = config?.[`${key}_type`] as FieldType | undefined;

            if (!type || !(type in fieldTypeComponents)) continue;

            const label = config?.[`${key}_name`] || key;
            const valuesList = config?.[`${key}_values`] || '';
            const options = valuesList ? valuesList.split(',') : [];

            let value: FieldValue = data?.[key];

            if (type === '4') {
                // Date parsing
                if (typeof value === 'string') {
                    try {
                        const normalized = normalizeDateString(value);
                        if (normalized) {
                            value = parseDate(normalized);
                        }
                    } catch {
                        value = null;
                    }
                }
            } else if (type === '5') {
                value = data?.[`${key}_id`] || '';
            }

            const renderer = fieldTypeComponents[type];
            if (renderer) {
                fields.push(
                    renderer(
                        key,
                        label,
                        value,
                        (fieldKey, val) => {
                            onChange({ ...data, [fieldKey]: val });
                        },
                        options
                    )
                );
            }
        }

        return fields;
    };

    return (
        <Flex direction="row" gap="size-100" wrap>
            {renderFields()}
        </Flex>
    );
};

export default DynamicProductTypeFields;
